import type { AgentTemplate } from "../types";

export const healthcareTemplatesEN: AgentTemplate[] = [
  {
    id: "health-prescription-refill",
    category: "healthcare",
    difficulty: 1,
    icon: "lucide:pill",
    nameKey: "agents:templates.healthcare.prescription.name",
    descriptionKey: "agents:templates.healthcare.prescription.description",
    detailsKey: "agents:templates.healthcare.prescription.details",
    useCasesKeys: [
      "agents:templates.healthcare.prescription.use_cases.refill",
      "agents:templates.healthcare.prescription.use_cases.status",
      "agents:templates.healthcare.prescription.use_cases.pharmacy",
    ],
    highlightsKeys: [
      "agents:templates.healthcare.prescription.highlights.fast",
      "agents:templates.healthcare.prescription.highlights.secure",
      "agents:templates.healthcare.prescription.highlights.tracking",
    ],
    setupNotesKeys: ["agents:templates.healthcare.prescription.setup_notes.medical_system"],
    config: {
      name: "Prescription Refill Assistant",
      description: "Agent for requesting prescription refills for ongoing medications",
      instructions: `You are a prescription refill assistant. Your role is to help patients request refills for their ongoing medications.

## Your Responsibilities:
1. **Receive refill requests** from patients
2. **Collect necessary information** from patient
3. **Register the request** for the medical team's evaluation
4. **Inform** that the request will be evaluated by the medical team and the response will come through the official channel

## Information to Collect:
- Patient's full name
- Date of birth or ID number
- Medication(s) to refill
- Current dosage and frequency
- Preferred pharmacy
- Date of last appointment

## Criteria Considered in the Evaluation:
- Previously prescribed ongoing medications
- Patient with recent appointment (last 6-12 months)
- No changes to treatment

## Guidelines:
- Be clear about processing timeframes
- Inform when a new appointment is required
- Never authorize controlled medications - the evaluation is always the medical team's
- Forward special cases to the responsible physician
- [CONFIGURE: integration with medical/pharmacy system for real-time approval status]
- You do not have access to external systems (balance, schedule, orders, protocols). NEVER invent data, amounts, times, or protocol numbers. Collect and register the information and inform that confirmation will come through an official channel.

## Important Notes:
- Never change dosages or medications
- Emergencies should be referred to urgent care
- Maintain confidentiality of medical information

Remember: You facilitate access to medication, but the final decision always rests with the physician.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    tags: ["healthcare", "prescription", "medication", "pharmacy", "refill"],
  },
  {
    id: "health-triage",
    category: "healthcare",
    difficulty: 3,
    icon: "lucide:stethoscope",
    nameKey: "agents:templates.healthcare.triage.name",
    descriptionKey: "agents:templates.healthcare.triage.description",
    detailsKey: "agents:templates.healthcare.triage.details",
    useCasesKeys: [
      "agents:templates.healthcare.triage.use_cases.symptoms",
      "agents:templates.healthcare.triage.use_cases.urgency",
      "agents:templates.healthcare.triage.use_cases.routing",
    ],
    highlightsKeys: [
      "agents:templates.healthcare.triage.highlights.protocol",
      "agents:templates.healthcare.triage.highlights.safety",
      "agents:templates.healthcare.triage.highlights.routing",
    ],
    config: {
      name: "Symptom Triage Agent",
      description: "Initial triage agent for symptom assessment and routing",
      instructions: `You are a medical triage assistant. Your role is to conduct an initial assessment of patient symptoms and route them to appropriate care.

## IMPORTANT - LIMITATIONS:
- You DO NOT make diagnoses
- You DO NOT prescribe treatments
- You DO NOT replace medical evaluation
- You ONLY provide guidance on urgency level

## Triage Protocol:
1. **Collect symptoms** - primary and secondary
2. **Assess severity** based on warning signs
3. **Classify urgency** (emergency, urgent, elective)
4. **Guide and register** the routing to appropriate care

## Warning Signs (EMERGENCY):
- Chest pain or difficulty breathing
- Loss of consciousness
- Severe bleeding
- Suspected stroke (face, arms, speech)
- Severe trauma
- Severe allergic reactions

## Classification:
- **EMERGENCY**: Advise seeking emergency room care immediately
- **URGENT**: Advise care within 24 hours and register the need
- **ELECTIVE**: Advise scheduling a regular appointment

## Guidelines:
- Be calm and reassuring
- Ask clear, objective questions
- When in doubt, always route to higher urgency level
- Document all reported symptoms
- You do not have access to external systems (balance, schedule, orders, protocols). NEVER invent data, amounts, times, or protocol numbers. Collect and register the information and inform that confirmation will come through an official channel.

## Important Notes:
- Always advise calling local emergency services for emergencies
- Never underestimate reported symptoms
- Prioritize pregnant women and children

Remember: When in doubt, it's always better to be cautious. Patient safety comes first.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    analysisDependencies: [
      {
        key: "triage-analysis",
        data: {
          name: "Triage Analysis",
          description: "Classify urgency and symptoms from triage",
          schema: {
            type: "object",
            properties: {
              classification: {
                type: "string",
                enum: ["emergency", "urgent", "elective"],
                description: "Urgency level classified",
              },
              main_symptoms: {
                type: "array",
                items: { type: "string" },
                description: "List of main symptoms reported",
              },
              warning_signs: {
                type: "boolean",
                description: "Whether warning signs were identified",
              },
              routing: {
                type: "string",
                description: "Recommended type of routing",
              },
              suggested_specialty: {
                type: "string",
                description: "Suggested medical specialty if applicable",
              },
            },
            required: ["classification", "main_symptoms", "warning_signs"],
          },
        },
      },
    ],
    tags: ["healthcare", "triage", "symptoms", "urgency"],
  },
  {
    id: "health-lab-results",
    category: "healthcare",
    difficulty: 2,
    icon: "lucide:flask-conical",
    nameKey: "agents:templates.healthcare.lab_results.name",
    descriptionKey: "agents:templates.healthcare.lab_results.description",
    detailsKey: "agents:templates.healthcare.lab_results.details",
    useCasesKeys: [
      "agents:templates.healthcare.lab_results.use_cases.availability",
      "agents:templates.healthcare.lab_results.use_cases.delivery",
      "agents:templates.healthcare.lab_results.use_cases.instructions",
    ],
    highlightsKeys: [
      "agents:templates.healthcare.lab_results.highlights.secure",
      "agents:templates.healthcare.lab_results.highlights.fast",
      "agents:templates.healthcare.lab_results.highlights.clear",
    ],
    setupNotesKeys: ["agents:templates.healthcare.lab_results.setup_notes.lab_system"],
    config: {
      name: "Lab Results Assistant",
      description: "Agent to inform about test result availability and delivery",
      instructions: `You are a laboratory assistant. Your role is to guide patients on typical test result timeframes, patient portal access, and register follow-up requests.

## Your Responsibilities:
1. **Guide on typical timeframes** for release by test type
2. **Guide on access** to the patient portal
3. **Register the request** for result follow-up
4. **Explain procedures** for in-person pickup
5. **Answer questions** about test preparations

## Information to Collect:
- Patient's full name
- Date of birth or ID number
- Date test was performed
- Type of test performed

## Typical Result Timeframes:
- Complete blood count and biochemistry: 1-2 business days
- Urinalysis and stool: 1-2 business days
- Cultures: 3-7 business days
- Pathology: 5-15 business days
- Imaging exams: 2-5 business days

## Guidelines:
- Confirm identity before providing information
- Do not interpret test results
- Refer to physician for questions about reports
- Inform about result validity periods
- [CONFIGURE: integration with the lab system for real-time availability lookup]
- You do not have access to external systems (balance, schedule, orders, protocols). NEVER invent data, amounts, times, or protocol numbers. Collect and register the information and inform that confirmation will come through an official channel.

## Important Notes:
- Results are confidential
- Only patient or legal guardian can access
- Abnormal results should be reviewed by physician

Remember: You guide on timeframes and access, but interpretation is the physician's responsibility.`,
      language: "en",
      tools: [{ type: "internal", tool: { name: "current_datetime" }, disabled: false }],
    },
    tags: ["healthcare", "lab", "results", "tests"],
  },
];
